import { Button, Row, Col, Spin, Form, Card, Radio, Table, message, Input, Select, Typography } from 'antd';
import { calculateExactAge, getUserDetails, makePostCall, parseMlcInfo } from '../../utils/helper';
import React, { useEffect, useMemo, useState } from 'react';
import TextArea from 'antd/es/input/TextArea';
import { MedicineBoxOutlined, UserOutlined, FileTextOutlined, AlertOutlined, FlagOutlined } from '@ant-design/icons';
import './order-info.scss';
import { FieldLabels, getPacsStatusLabel } from '../../constants/fieldLabels';

const { Link } = Typography;


const OrderInfoPopup = ({ data, closeModal, refreshTable }) => {

  const patInfo = data?.pacs_order?.patient;
  const ordInfo = data?.pacs_order;

  const [scans, setScans] = useState({ data: [], loading: false });
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [physicianNotes, setPhysicianNotes] = useState('');
  const [priority, setPriority] = useState('NORMAL');

  // MLC state
  const [isMlc, setIsMlc] = useState<boolean | null>(null);
  const [showMlcExtra, setShowMlcExtra] = useState(false);
  const [mlcNumber, setMlcNumber] = useState('');
  const [mlcCategory, setMlcCategory] = useState<string | undefined>(undefined);
  const [mlcStation, setMlcStation] = useState('');
  const [mlcFir, setMlcFir] = useState('');
  const [mlcRemarks, setMlcRemarks] = useState('');
  const [mlcSaving, setMlcSaving] = useState(false);

  useEffect(() => {
    if (ordInfo?.pacs_ord_id) {
      fetchAllScans(ordInfo.pacs_ord_id);
    }

    if (ordInfo?.po_priority) {
      setPriority(ordInfo.po_priority);
    }

    if (ordInfo) {
      const isCurrentlyMlc = ordInfo.po_is_mlc === true;
      setIsMlc(isCurrentlyMlc ? true : false);
      if (isCurrentlyMlc) {
        const info = parseMlcInfo(ordInfo.po_mlc_info);
        setMlcNumber(ordInfo.po_mlc_number || '');
        setMlcCategory(info.category || undefined);
        setMlcStation(info.station || '');
        setMlcFir(info.fir || '');
        setMlcRemarks(info.remarks || '');
        if (ordInfo.po_mlc_number || info.category || info.station || info.fir || info.remarks) {
          setShowMlcExtra(true);
        }
      }
    }
  }, [ordInfo]);

  const fetchAllScans = async (pacs_ord_id) => {
    // fetch scans from API
    await makePostCall('/get-all-scans', { pacs_ord_id: pacs_ord_id }).then((res) => {
      setScans({ data: res.data.data, loading: false });
    }).catch((err) => {
      setScans({ data: [], loading: false });
    });
  }

  const scanColumns = [
    {
      title: FieldLabels.MODALITY,
      dataIndex: 'po_modality',
    },
    {
      title: FieldLabels.ITEM_CODE,
      dataIndex: 'po_diag_no'
    },
    {
      title: FieldLabels.DESCRIPTION,
      dataIndex: 'po_diag_desc',
    },
    {
      title: FieldLabels.ACCESSION_NUMBER_SHORTEST,
      dataIndex: 'po_acc_no',
    },
    {
      title: FieldLabels.STATUS,
      render: (itm, rec) => (
        <span>
          {getPacsStatusLabel(rec?.order_workflow?.ow_pacs_status)}
        </span>
      )
    }
  ];

  const handlePriorityChange = (e) => {
    setPriority(e.target.value);
  }

  const mlcChanged = useMemo(() => {
    const prevIsMlc = ordInfo?.po_is_mlc === true;
    if (isMlc === null) return false;
    if (isMlc !== prevIsMlc) return true;
    if (!isMlc) return false;
    const info = parseMlcInfo(ordInfo?.po_mlc_info);
    return (
      mlcNumber !== (ordInfo?.po_mlc_number || '') ||
      (mlcCategory || undefined) !== (info.category || undefined) ||
      mlcStation !== (info.station || '') ||
      mlcFir !== (info.fir || '') ||
      mlcRemarks !== (info.remarks || '')
    );
  }, [isMlc, mlcNumber, mlcCategory, mlcStation, mlcFir, mlcRemarks, ordInfo]);

  const checkButtonEnable = useMemo(() => {
    const prevPriority = ordInfo.po_priority || 'NORMAL';
    if (!!clinicalNotes && clinicalNotes.length > 0 ||
      !!physicianNotes && physicianNotes.length > 0 ||
      (priority !== prevPriority) ||
      mlcChanged
    ) {
      return false
    } else {
      return true
    }
  }, [priority, clinicalNotes, physicianNotes, mlcChanged]);

  const handleSubmit = async () => {
    const prevPriority = ordInfo.po_priority || 'NORMAL';
    const priorityChanged = priority !== prevPriority;

    let success = false;

    try {
      if (priorityChanged) {
        const payload = {
          pacs_ord_id: ordInfo.pacs_ord_id,
          priority: priority,
        };
        await makePostCall('/update-his-order-priority', payload);
        message.success("Priority updated successfully");
        success = true;
      }

      if (!!clinicalNotes || !!physicianNotes) {
        const formData = new FormData();
        const orderIds = scans?.data?.map(itm => itm.pacs_ord_id);
        formData.append('order_ids', orderIds.join(','));
        formData.append('type', 'notes');
        formData.append('notes', clinicalNotes);
        formData.append('user_id', getUserDetails()?.username);

        await makePostCall('/upload-notes', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        message.success("Notes added successfully");
        success = true;
      }

      if (mlcChanged) {
        setMlcSaving(true);
        const shouldMark = isMlc === true;
        const payload = {
          order_id: ordInfo.pacs_ord_id,
          user_id: getUserDetails()?.username,
          markMlc: shouldMark,
          mlc_number: shouldMark ? (mlcNumber.trim() || null) : null,
          mlc_info: shouldMark
            ? { category: mlcCategory || null, station: mlcStation.trim() || null, fir: mlcFir.trim() || null, remarks: mlcRemarks.trim() || null }
            : null,
        };
        await makePostCall('/mark-mlc', payload);
        message.success(shouldMark ? 'Marked as MLC' : 'MLC marking removed');
        setMlcSaving(false);
        success = true;
      }

      if (success) {
        refreshTable?.();
        closeModal?.();
      }
    } catch (e) {
      setMlcSaving(false);
      message.error('Something went wrong');
      console.error("Error during update", e);
    }
  };


  return (
    <div className="order-info-container">
      <Card
        title={
          <span>
            <UserOutlined /> Patient Information
          </span>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 8]}>
          <Col span={12}>
            <b>{FieldLabels.PATIENT_NAME}:</b> {patInfo?.pat_name}
          </Col>
          <Col span={12}>
            <b>{FieldLabels.PATIENT_ID}:</b> {patInfo?.pat_pin}
          </Col>
          <Col span={12}>
            <b>{FieldLabels.PATIENT_AGE}:</b> {calculateExactAge(patInfo?.pat_dob)}
          </Col>
          <Col span={12}>
            <b>{FieldLabels.PATIENT_REPORT_SEX}:</b> {patInfo?.pat_sex}
          </Col>
        </Row>
      </Card>

      <Card
        title={
          <span>
            <MedicineBoxOutlined /> Order Information
          </span>
        }
      >
        <Row gutter={[16, 8]}>
          <Col span={12}>
            <b>Order No:</b> {ordInfo?.po_his_ord_no}
          </Col>
          <Col span={12}>
            <b>Site:</b> {data?.pacs_order?.po_site || 'SOMAJIGUDA'}
          </Col>
          <Col span={24}>
            <b>Priority:</b>
            <Radio.Group
              onChange={handlePriorityChange}
              value={priority}
              style={{ marginLeft: 12 }}
            >
              <Radio value="NORMAL">Normal</Radio>
              <Radio value="EMERGENCY">Emergency</Radio>
              <Radio value="ASAP">ASAP</Radio>
            </Radio.Group>
          </Col>
          <Col span={24}>
            <b><FlagOutlined style={{ marginRight: 4 }} />Medico-Legal Case (MLC):</b>
            <Radio.Group
              value={isMlc}
              onChange={e => {
                setIsMlc(e.target.value);
                if (!e.target.value) setShowMlcExtra(false);
              }}
              style={{ marginLeft: 12 }}
            >
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
            {isMlc && (
              <span style={{ marginLeft: 12 }}>
                {!showMlcExtra ? (
                  <Link onClick={() => setShowMlcExtra(true)}>Add extra information</Link>
                ) : (
                  <Link onClick={() => setShowMlcExtra(false)}>Hide extra information</Link>
                )}
              </span>
            )}
            {isMlc && showMlcExtra && (
              <div style={{ marginTop: 12, padding: '12px 16px', background: '#fafafa', borderRadius: 6, border: '1px solid #e8e8e8' }}>
                <Row gutter={[12, 12]}>
                  <Col span={12}>
                    <div style={{ marginBottom: 4 }}><b>MLC Number</b></div>
                    <Input
                      placeholder="Optional — register number"
                      value={mlcNumber}
                      onChange={e => setMlcNumber(e.target.value)}
                      allowClear
                    />
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 4 }}><b>Category / Type</b></div>
                    <Select
                      allowClear
                      showSearch
                      placeholder="Optional"
                      value={mlcCategory}
                      onChange={val => setMlcCategory(val)}
                      style={{ width: '100%' }}
                      options={[
                        { value: 'RTA', label: 'Road Traffic Accident (RTA)' },
                        { value: 'Assault', label: 'Assault' },
                        { value: 'Burns', label: 'Burns' },
                        { value: 'Poisoning', label: 'Poisoning' },
                        { value: 'Suicide Attempt', label: 'Suicide Attempt' },
                        { value: 'Sexual Assault', label: 'Sexual Assault' },
                        { value: 'Fall', label: 'Fall' },
                        { value: 'Other', label: 'Other' },
                      ]}
                    />
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 4 }}><b>Police Station</b></div>
                    <Input
                      placeholder="Optional"
                      value={mlcStation}
                      onChange={e => setMlcStation(e.target.value)}
                      allowClear
                    />
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 4 }}><b>FIR Number</b></div>
                    <Input
                      placeholder="Optional"
                      value={mlcFir}
                      onChange={e => setMlcFir(e.target.value)}
                      allowClear
                    />
                  </Col>
                  <Col span={24}>
                    <div style={{ marginBottom: 4 }}><b>Remarks</b></div>
                    <TextArea
                      rows={2}
                      placeholder="Optional — additional details"
                      value={mlcRemarks}
                      onChange={e => setMlcRemarks(e.target.value)}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </Col>
        </Row>
        <div className="mt-3">
          <Table
            columns={scanColumns}
            dataSource={scans?.data}
            pagination={false}
          />
        </div>

        <div className="separator"></div>

        <div className="mt-2">
          <span>Clinical Notes</span>
          <TextArea
            onChange={e => {
              setClinicalNotes(e.target.value);
            }}
          />
        </div>

        {/* <div className='mt-2'>
          <span>Physician Notes</span>
          <TextArea onChange={(e) => {
            setPhysicianNotes(e.target.value);
          }} />
        </div> */}

        <div className="mt-2 text-center">
          <Button
            type="primary"
            disabled={checkButtonEnable}
            loading={mlcSaving}
            onClick={handleSubmit}
          >
            UPDATE INFO
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default OrderInfoPopup;
